import { Component, Input, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Event } from 'src/app/common/events/event-base';
import { UtilsService } from 'src/app/common/services/utils.service';
import { BaseSearchResultsComponent } from '../../../../components/seo/base-search-results/base-search-results.component';
import { SortEvent } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { CompetitorTrendChartComponent } from 'src/app/components/seo/competitor-trend-chart/competitor-trend-chart.component';
import { CapitalizeFirstLetterPipe } from 'src/app/common/pipes/extract.capitalize.first.letter.pipe';

@Component({
  selector: 'gig-keyword-results',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, ButtonModule, ChartModule, TranslateModule, DialogModule, CheckboxModule, TooltipModule, InputTextModule, RippleModule, CompetitorTrendChartComponent, CapitalizeFirstLetterPipe],
  templateUrl: './keyword-results.component.html',
  styleUrls: ['./keyword-results.component.scss']
})
export class KeywordResultsComponent extends BaseSearchResultsComponent implements OnInit {

  @Output() keywordSelected: EventEmitter<KeywordResultsEvent> = new EventEmitter<KeywordResultsEvent>();
  @Output() pageChanged: EventEmitter<KeywordResultsEvent> = new EventEmitter<KeywordResultsEvent>();
  @Input() isKeywordSearch: boolean;
  @Input() resultCases: any;
  @Input() selectedSearchType: string;
  @Input() chartData: any;
  @Input() chartOptions: any;
  @Input() searchResults: any[];
  @Input() showAnalysisModal: boolean = false;

  public customSortField: string = '';
  public customSortOrder: number = 1; // 1 for ascending, -1 for descending
  public openAnalysisModal: boolean = false;
    isHovered: boolean = false;

  constructor(public utils: UtilsService) {
    super(utils);
  }

    ngOnInit(): void {
    this.cols = [
        { field: 'keyword', header: 'Keyword'},
        { field: 'volume', header: 'Volume'},
        { field: 'cpc', header: 'CPC'},
        { field: 'kd', header: 'KD' },
        { field: 'competition', header: 'Competition' },
        { field: 'backlinks', header: 'Avg. Backlinks' },
        { field: 'dofollow', header: 'Avg. Dofollow' },
        { field: 'referringPages', header: 'Avg. RP' },
        { field: 'referringDomains', header: 'Avg. RD' },
        { field: 'referringMainDomains', header: 'Avg. Main Domains' },
        { field: 'rank', header: 'Avg. Rank' },
        { field: 'mainDomainRank', header: 'Avg. Domain Rank'}
    ]

    this.searchResults = [];
  }

  onMouseOver(): void {
    this.isHovered = true;
  }

  onMouseOut(): void {
    this.isHovered = false;
  }

  onTouchStart(): void {
    this.isHovered = true;
  }

  onTouchEnd(): void {
    this.isHovered = false;
  }
  onOpenAnalysisModal(): void {
    this.openAnalysisModal = true;
  }
  public onSelectedKeywordClicked(e: any): void {
    this.utils.logger.info('KeywordResultsComponent.onSelectedKeywordClicked() ');
    this.keywordSelected.emit(new KeywordResultsEvent('KeywordResults.KEYWORD_CLICKED', e));
  }

  public export(): void {
    this.utils.logger.info('KeywordResultsComponent.export() ');
    const options = {selectionOnly: false, fileName: 'keywords.xlsx'};
    this.resultTable.exportCSV(options);
  }

  public resetTable(): void {
    this.utils.logger.info('KeywordResultsComponent.resetTable() ');
    this.resultTable.reset();
  }

  public getKeyword(record: any): any {
    return record?.keyword ?? record?.keywordData?.keyword;

  }

  trackById(index: number, item: any): any {
    return item.id; // Assuming each row has a unique 'id'
  }

  public customSort(event: SortEvent): void {
    this.utils.logger.info('KeywordResultsComponent.customSort()');
    this.customSortField = event.field;
    this.customSortOrder = this.customSortOrder * -1; // Toggle between ascending and descending

    //adding a comment
    if (event.field === 'keyword') {
      event.data.sort((a, b) => {
        const keywordA = a?.keyword?.toLowerCase() ?? a?.keywordData?.keyword;
        const keywordB = b?.keyword?.toLowerCase() ?? b?.keywordData?.keyword;
        const comparison = keywordA?.toLowerCase().localeCompare(keywordB?.toLowerCase());
        return event.order === 1 ? comparison : -comparison;
      });
    } else if (event.field === 'intent') {
      event.data.sort((a, b) => {
        const intentA = a?.searchIntentInfo?.mainIntent ?? a?.keywordData?.searchIntentInfo?.mainIntent;
        const intentB = b?.searchIntentInfo?.mainIntent ?? b?.keywordData?.searchIntentInfo?.mainIntent;
        const comparison = intentA?.toLowerCase().localeCompare(intentB?.toLowerCase());
        return event.order === 1 ? comparison : -comparison;
      });

    }
    else {
      event.data.sort((a, b) => {
        let valueA;
        let valueB;

        switch (event.field) {
            case 'volume':
                valueA = a?.keywordData?.keywordInfo?.searchVolume ?? a?.keywordInfo?.searchVolume;
                valueB = b?.keywordData?.keywordInfo?.searchVolume ?? b?.keywordInfo?.searchVolume;
                break;
            case 'cpc':
                valueA = a?.keywordData?.keywordInfo?.cpc ?? a?.keywordInfo?.cpc;
                valueB = b?.keywordData?.keywordInfo?.cpc ?? b?.keywordInfo?.cpc;
                valueA = this.getCpcValue(valueA);
                valueB = this.getCpcValue(valueB);
                break;
            case 'kd':
                valueA = a?.keywordData?.keywordProperties?.keywordDifficulty ?? a?.keywordProperties?.keywordDifficulty;
                valueB = b?.keywordData?.keywordProperties?.keywordDifficulty ?? b?.keywordProperties?.keywordDifficulty;
                break;
            case 'competition':
                valueA = a?.keywordData?.keywordInfo?.competition ?? a?.keywordInfo?.competition;
                valueB = b?.keywordData?.keywordInfo?.competition ?? b?.keywordInfo?.competition;
                break;
            case 'backlinks':
                valueA = a?.avgBacklinksInfo?.backlinks ?? a?.keywordData?.avgBacklinksInfo?.backlinks;
                valueB = b?.avgBacklinksInfo?.backlinks ?? b?.keywordData?.avgBacklinksInfo?.backlinks;
                break;
            case 'dofollow':
                valueA = a?.avgDofollowInfo?.dofollow ?? a?.keywordData?.avgBacklinksInfo?.dofollow;
                valueB = b?.avgDofollowInfo?.dofollow ?? b?.keywordData?.avgBacklinksInfo?.dofollow;
                break;
            case 'referringPages':
                valueA = a?.avgBacklinksInfo?.referringPages ?? a?.keywordData?.avgBacklinksInfo?.referringPages;
                valueB = b?.avgBacklinksInfo?.referringPages ?? b?.keywordData?.avgBacklinksInfo?.referringPages;
                break;
            case 'referringDomains':
                valueA = a?.avgBacklinksInfo?.referringDomains ?? a?.keywordData?.avgBacklinksInfo?.referringDomains;
                valueB = b?.avgBacklinksInfo?.referringDomains ?? b?.keywordData?.avgBacklinksInfo?.referringDomains;
                break;
            case'referringMainDomains':
                valueA = a?.avgBacklinksInfo?.referringMainDomains ?? a?.keywordData?.avgBacklinksInfo?.referringMainDomains;
                valueB = b?.avgBacklinksInfo?.referringMainDomains ?? b?.keywordData?.avgBacklinksInfo?.referringMainDomains;
                break;
            case 'rank':
                valueA = a?.avgBacklinksInfo?.rank ?? a?.keywordData?.avgBacklinksInfo?.rank;
                valueB = b?.avgBacklinksInfo?.rank ?? b?.keywordData?.avgBacklinksInfo?.rank;
                break;
            case'mainDomainRank':
                valueA = a?.avgBacklinksInfo?.mainDomainRank ?? a?.keywordData?.avgBacklinksInfo?.mainDomainRank;
                valueB = b?.avgBacklinksInfo?.mainDomainRank ?? b?.keywordData?.avgBacklinksInfo?.mainDomainRank;
                break;


        }

        if (valueA < valueB) {
            return -1 * this.customSortOrder;
        } else if (valueA > valueB) {
            return 1 * this.customSortOrder;
        } else {
            return 0;
        }
    });
      }
  }

  public getCpcValue(cpc: any): number {
    const cpcValue = cpc ? cpc : 0;
    return parseFloat(cpcValue.toString().replace(/[^0-9.-]+/g, ""));
  }

  public onPageChanged(e: any): void {
    this.utils.logger.info('KeywordResultsComponent.onPageChanged() ');
    this.pageChanged.emit(new KeywordResultsEvent('KeywordResults.PAGE_CHANGED', e));
  }

  public getIntentClasses(result: any): string {
    let classes = 'w-4 text-center border-none border-primary-500 border-round';
    const intent = result?.searchIntentInfo?.mainIntent ?? result?.keywordData?.searchIntentInfo?.mainIntent;
    if (intent == 'transactional') {
      classes += ' green';
    } else if (intent == 'informational') {
      classes += ' orange';
    } else if (intent == 'navigational') {
      classes += ' gold';
    } else if (intent == 'commercial') {
      classes += ' light-green';
    }
    return classes;
  }
}

export class KeywordResultsEvent extends Event {
  constructor(type: string, payload: any) {
    super(type, payload);
  }
}
